// Domain types + JSON mapping. Backend speaks snake_case; we map to camelCase
// ONCE here (mirrors the Flutter *Model.fromJson factories) so nothing else in
// the app touches raw snake_case keys.

export type ElectionStatus = "ongoing" | "ended" | "upcoming";

export interface Voter {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  qrCode: string | null;
}

export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
}

export interface Election {
  id: string;
  name: string;
  description: string;
  status: ElectionStatus;
  startDate: string; // ISO
  endDate: string; // ISO
  requiredVotesCount: number;
  hasVoted: boolean;
  candidates: Candidate[];
}

export const fullName = (p: { firstName: string; lastName: string }) =>
  `${p.firstName} ${p.lastName}`.trim();

// ---- JSON mappers (snake_case -> camelCase) ----

export function voterFromJson(j: Record<string, unknown>): Voter {
  return {
    id: String(j.id),
    firstName: (j.first_name as string) ?? "",
    lastName: (j.last_name as string) ?? "",
    phone: (j.phone as string) ?? "",
    qrCode: (j.qr_code as string) ?? null,
  };
}

// Only http(s) image URLs are allowed; anything else (javascript:, data:, junk)
// becomes "" so a compromised/misconfigured backend can't inject a hostile src.
function safePhotoUrl(raw: string): string {
  const fixed = raw.replace(/(?<!:)\/\/+/g, "/"); // collapse accidental double slashes
  try {
    const u = new URL(fixed);
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : "";
  } catch {
    return "";
  }
}

// Election candidates use { id, first_name, last_name, photo_url }.
function candidateFromElectionJson(j: Record<string, unknown>): Candidate {
  const photoUrl = safePhotoUrl((j.photo_url as string) ?? "");
  return {
    id: String(j.id),
    firstName: (j.first_name as string) ?? "",
    lastName: (j.last_name as string) ?? "",
    photoUrl,
  };
}

function parseStatus(s: string): ElectionStatus {
  switch (s?.toLowerCase()) {
    case "ongoing":
      return "ongoing";
    case "ended":
    case "completed":
      return "ended";
    default:
      return "upcoming";
  }
}

// Accepts either the raw election object or a { data: {...} } envelope.
export function electionFromJson(input: Record<string, unknown>): Election {
  const j = (input.data as Record<string, unknown>) ?? input;
  return {
    id: String(j.id),
    name: (j.name as string) ?? "",
    description: (j.description as string) ?? "",
    status: parseStatus(j.status as string),
    startDate: j.start_date as string,
    endDate: j.end_date as string,
    requiredVotesCount: (j.required_votes_count as number) ?? 0,
    hasVoted: (j.has_voted as boolean) ?? false,
    candidates: ((j.candidates as Record<string, unknown>[]) ?? []).map(
      candidateFromElectionJson,
    ),
  };
}

// ---- Typed API errors (ported from vote_remote_datasource.dart error map) ----

export type ApiErrorCode =
  | "auth" // 401 / auth required / session expired
  | "already_voted" // 403 "already voted" / 409
  | "election_not_active" // 403 "not active"
  | "invalid_candidate_count" // 422 "exactly N candidates"
  | "duplicate_candidates" // 422 "duplicate"
  | "not_registered" // 404 on login: phone not a voter
  | "validation" // 422 generic
  | "network" // no response (timeout / offline)
  | "server"; // 5xx / unknown

export class ApiError extends Error {
  code: ApiErrorCode;
  status?: number;
  constructor(code: ApiErrorCode, message: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}
